// SPDX-License-Identifier: Apache-2.0
import GirafeSingleton from '../../base/GirafeSingleton';
import { IUrlShortener } from './iurlshortener';
import GmfShareManager from '../../components/share/tools/gmfsharemanager';
import GeoGirafeShareManager from '../../components/share/tools/geogirafesharemanager';

class ShareManager extends GirafeSingleton {
  private urlShortener?: IUrlShortener;

  public get UrlShortener(): IUrlShortener | undefined {
    return this.urlShortener;
  }

  public override initializeSingleton() {
    const share = this.context.configManager.Config.share;

    if (share) {
      switch (share.service) {
        case 'gmf':
          this.urlShortener = new GmfShareManager(share.createUrl);
          break;
        case 'geogirafe':
          this.urlShortener = new GeoGirafeShareManager(share.createUrl, this.context.urlManager);
          break;
      }
    }
  }

  public getStateToShare() {
    const encodedState = this.context.stateSerializer.getSerializedState();
    return encodedState;
  }

  public hasSharedState() {
    const encodedState = this.getStateFromUrl();
    return encodedState !== null;
  }

  private getStateFromUrl(): string | null {
    if (this.context.sessionManager.hasState()) {
      return null;
    }
    return this.context.urlManager.getHash();
  }

  public async setStateFromUrl(): Promise<boolean> {
    // NOTE: This method should only be called when themes.json has been loaded
    // (i.e. from the ThemesManager), because it needs the themes.
    let encodedState = this.getStateFromUrl();
    let stateRestored = false;
    if (encodedState) {
      if (encodedState.startsWith('gg-')) {
        // The hash contains a shortlink identifier.
        // We first have to load the hash from the GMF server
        encodedState = await this.getStateFromServer(encodedState);
      }
      stateRestored = this.context.stateSerializer.deserializeAndSetState(encodedState);
    }
    return stateRestored;
  }

  private async getStateFromServer(geogirafeState: string): Promise<string> {
    if (
      this.context.configManager.Config.share?.service !== 'geogirafe' ||
      !this.context.configManager.Config.share.getUrl
    ) {
      throw new Error('We get a geogirafe state but the configuration is not correct.');
    }
    let getUrl = this.context.configManager.Config.share.getUrl;
    if (!getUrl.endsWith('/')) {
      getUrl += '/';
    }
    getUrl += geogirafeState.substring(3);
    const resp = await fetch(getUrl);
    const json = await resp.json();
    const compressedState = json.long_url.split('#')[1];
    return compressedState;
  }
}

export default ShareManager;
